import { Identifiable, RecordModel } from "../core";
import { ILocation } from "./geo";
import { GroupModel, IUser } from "./auth";
import moment from "moment/moment";
import RecordSelect from "../../components/fields/RecordSelect";
import React, { useState } from "react";
import { Form, Input, Tag, Tooltip, Button } from "antd";
import TipTapEditor from "../../components/blog/TipTapEditor";
import GalleryImagePicker from "../../components/blog/GalleryImagePicker";
import { seoColumn } from "../../components/business/seo/seoColumn";

export interface ICmsCategory extends Identifiable {
  code: string;
  title: any; // LocalizedTextFields
  description: any; // LocalizedTextFields
  position: number;
  is_active: boolean;
}

export interface ISubcategory extends Identifiable {
  category: ICmsCategory;
  code: string;
  position: number;
  is_active: boolean;
  title: any; // LocalizedTextFields
  description: any; // LocalizedTextFields
}

export interface ITag extends Identifiable {
  code: string;
  title: any; // LocalizedTextFields
}

export interface IPost extends Identifiable {
  wp_id: number | null;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  author: IUser;
  status: "draft" | "publish" | "pending" | "private";
  published_at: string | null;
  language: string;
  meta_description: string;
  category: ICmsCategory | null;
  subcategory: ISubcategory | null;
  location: ILocation | null;
  tags: ITag[];
  created_at: string;
  modified_at: string;
  wp_cover_image_url?: string;
}

export const CmsCategoryModel: RecordModel<ICmsCategory> = {
  name: "Category",
  namePlural: "Categories",
  path: "cms/categories",
  selectQuery: "",
  tableQuery: "",
  toString: r => r.code,
  fieldModels: {},
  primaryKeyName: "code",
};

export const SubcategoryModel: RecordModel<ISubcategory> = {
  name: "Subcategory",
  namePlural: "Subcategories",
  path: "cms/subcategories",
  selectQuery: "&expand=category",
  tableQuery: "&expand=category",
  toString: r => `${r.category.code} -> ${r.code}`,
  fieldModels: {
    category: "CmsCategory",
  },
};

export const TagModel: RecordModel<ITag> = {
  name: "Tag",
  path: "cms/tags",
  selectQuery: "",
  tableQuery: "",
  toString: r => `#${r.code}`,
  fieldModels: {},
};

const omitPostFields = ['wp_id', 'wp_cover_image_url', 'slug', 'content', 'excerpt', 'author', 'meta_description', 'location', 'created_at', 'modified_at', 'published_at'];

const WpCoverImageSelector = (props: { name: string, label: string }) => {
  const [pickerVisible, setPickerVisible] = useState(false);
  const form = Form.useFormInstance();

  const handleSelectImage = (imageUrl: string) => {
    form.setFieldsValue({ [props.name]: imageUrl });
    setPickerVisible(false);
  };

  const imageUrl = Form.useWatch(props.name, form);

  return (
    <>
      <Form.Item label={props.label}>
        {imageUrl && <img src={imageUrl} alt="Cover" style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }} />}
        <Form.Item name={props.name} noStyle>
            <Input
              readOnly
              placeholder="Click Select to choose an image"
              addonAfter={<Button onClick={() => setPickerVisible(true)}>Select</Button>}
            />
        </Form.Item>
      </Form.Item>
      <GalleryImagePicker
        visible={pickerVisible}
        onSelect={handleSelectImage}
        onCancel={() => setPickerVisible(false)}
        selectSize="fhd"
      />
    </>
  );
};

export const PostModel: RecordModel<IPost> = {
  name: "Post",
  path: "cms/posts",
  selectQuery: "&expand=author,category,subcategory,location",
  tableQuery: "&expand=author,category,subcategory,location,tags",
  toString: r => r.title,
  primaryKeyName: 'slug',
  customTableMappings: {
    ...Object.fromEntries(omitPostFields.map(fieldName => ([fieldName, { omit: true }]))),
    category: { width: 150 },
    subcategory: { width: 150, render: post => (<>{post.subcategory?.code || post.location?.name || ''}</>) },
    tags: {
      omit: true,
      width: 140,
      // render: post => <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>{post.tags.map(tag => <Tag style={{ margin: 0 }}>#{tag.code}</Tag>)}</div>
    },
    language: { title: 'Lang', width: 50, render: r => <Tooltip title={r.language === 'en' ? 'English' : 'Spanish'}><span style={{ fontSize: 24 }}>{r.language === 'en' ? '🇺🇸' : '🇲🇽'}</span></Tooltip> },
    status: {
      render: post => (post.status === 'publish') ? (
        <>
          <div style={{ marginBottom: -4 }}>Published</div>
          <small>{moment(post.published_at).format('ll')}</small>
        </>
      ) : post.status,
      width: 90
    },
  },
  tableFilters: [
    { selectField: 'language' },
    { selectModel: CmsCategoryModel },
    { selectField: 'status' },
  ],
  allowSearch: true,
  fieldModels: {
    author: "User",
    category: "CmsCategory",
  },
  customCreateFields: [
    { name: 'wp_id', component: null },
    { name: 'wp_cover_image_url', component: () => <WpCoverImageSelector name="wp_cover_image_url" label="Cover Image" /> },
    { name: "tags", component: props => <RecordSelect required={false} label="Tags" formName="tags" mode="multiple" recordModel={TagModel} allowManage /> },
    { name: "title", component: null },
    { name: "content", component: null },
  ],
  createStartChildren: (
    <div style={{ flex: 30, minWidth: 620 }}>
      <Form.Item name="title" label="Title" rules={[{ required: true, max: 200 }]}>
        <Input maxLength={200} showCount />
      </Form.Item>
    <Form.Item
      name="content"
      label="Content"
    >
      <TipTapEditor />
    </Form.Item>
    </div>
  ),
  customActions: [
    {
      name: 'Unpublish',
      path: 'unpublish',
      isDetail: true,
    }
  ],
  formWrapperStyle: { display: 'flex', flexDirection: 'row', gap: 40, maxWidth: 1240, margin: 'auto' },
  createCenterFieldsStyle: { flex: 10 },
  drawerWidth: '100%',
  adjacentModels: [CmsCategoryModel, SubcategoryModel],
  showsChangeLog: true,
  extraColumns: [
    seoColumn<IPost>({
      pathFor: r => `/blog/${r.slug}`,
      labelFor: r => r.title,
      imageFor: r => r.wp_cover_image_url,
    }),
  ],
};
